import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ColorPowder } from '@/modules/ColorModule/interfaces/ColorPowder';
import { ColorTableData } from '@/modules/ColorModule/interfaces/ColorTableData';
import { RoltekColorCollections, MappedColorCodes } from '@/modules/ColorModule/helpers/ColorModuleHelper';
import { ColorChart } from '@/modules/ColorModule/interfaces/ColorChart';
import { stripNaturalWords } from '@/helpers/MaterialHelper';
import store from '@/store';
import i18n from '@/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'ColorModuleTable',
  props: {
    searchQuery: null,
    currentCollection: null,
    showLatestColors: { type: Boolean, default: false },
    pagination: null
  },
  emits: ['tableChange', 'selectColor', 'onQueryChange'],
  setup(__props: any, { emit }) {

const props = __props as { searchQuery?: string, currentCollection: string, showLatestColors: boolean, pagination: any };




const tableHeight = ref('575px');
const customColumns = computed(
    (): Array<any> => [
        {
            condition: 'STANDARD-COLLECTION',
            order: 1,
            columnData: { title: i18n.t('Kod'), dataIndex: 'colorCode', key: 'colorCode', width: 70 },
        },
    ],
);
const colors = computed((): Array<ColorPowder> => store.getters['colorData/colors']);
const latestColors = computed(
    (): Array<{ color: any; powder: ColorPowder }> => store.getters['colorData/latestColors'],
);

const onTableChange = (pagination: any, filters: any, sorter: any) => {
    emit('tableChange', pagination, filters, sorter);
};

const calculateTableHeight = () => {
    const baseHeight = 575;
    const baseMargin = 80;

    const tableEL = document.querySelector('.color-module-table');

    if (!tableEL) {
        return baseHeight + 'px';
    }

    const tableRect = tableEL.getBoundingClientRect();
    const calculatedTableHeight = tableRect.height;
    const tableMarginTop = parseInt(getComputedStyle(tableEL).marginTop, 10);

    if (tableMarginTop > baseMargin) {
        const newHeight = calculatedTableHeight + (tableMarginTop - baseMargin);

        return newHeight + 'px';
    }

    return baseHeight + 'px';
};

const removePaginationTooltips = () => {
    const paginationItems = document.querySelectorAll(
        '.ant-pagination-item, .ant-pagination-prev, .ant-pagination-next',
    );
    paginationItems.forEach((item) => {
        item.removeAttribute('title'); // Nuke the tooltip behavior
    });
};

const columns = computed(() => {
    const tableColumns = [
        {
            dataIndex: 'color',
            key: 'color',
            scopedSlots: { customRender: 'color' },
            slots: { title: 'colorTitle' }, // Use `slots` for title
            width: 70,
        },
        {
            dataIndex: 'colorName',
            key: 'colorName',
            scopedSlots: { customRender: 'colorName' },
            slots: { title: 'colorNameTitle' }, // Use `slots` for title
            width: 300,
        },
        { title: i18n.t('Gloss/Surface'), dataIndex: 'colorSurface', key: 'colorSurface', width: 150 },
        {
            title: i18n.t('Manufacturer’s powder ID'),
            dataIndex: 'powderId',
            key: 'powderId',
            width: 370,
            scopedSlots: { customRender: 'powderId' },
        },
        {
            title: i18n.t(''),
            dataIndex: 'select',
            key: 'select',
            scopedSlots: { customRender: 'select' },
            width: 80,
        },
    ];

    // Insert custom columns based on conditions
    customColumns.value.forEach((customCol) => {
        if (customCol.condition === 'STANDARD-COLLECTION' && props.currentCollection === 'STANDARD') {
            tableColumns.splice(customCol.order, 0, customCol.columnData);
        }
    });

    return tableColumns;
});

const determineIsValidRoltekCollection = (colorChart: ColorChart | null | undefined): boolean => {
    if (!colorChart) {
        return false;
    }

    const collectionName = colorChart?.groupName || '';

    return RoltekColorCollections.has(collectionName as string);
};

const filterColorsBySeachQuery = (colorsToFilter: ColorTableData[]) => {
    const searchQuery = props.searchQuery as string;

    return colorsToFilter.filter((color: ColorTableData) => {
        switch (true) {
            case color?.colorName?.toLowerCase().includes(searchQuery.toLowerCase()):
                return true;
            case color?.colorSurface?.toLowerCase().includes(searchQuery.toLowerCase()):
                return true;
            case color?.powderId?.toLowerCase().includes(searchQuery.toLowerCase()):
                return true;
            default:
                return false;
        }
    });
};

const formatLatestColorsData = (): ColorTableData[] => {
    if (!latestColors.value || latestColors.value.length === 0) {
        return [];
    }

    const formattedColors: ColorTableData[] = [];

    for (let i = 0; i < latestColors.value.length; i++) {
        const latestColor = latestColors.value[i];
        const powder = latestColor.powder;
        const color = latestColor.color;

        const groupName = powder?.colorCollection?.groupName || '';
        const isRoltekColor = determineIsValidRoltekCollection(powder.colorCollection) && !!color?.ingeniousColor;
        const isStandard = isRoltekColor && groupName === 'STANDARD';
        const ingeniousColor = color?.ingeniousColor || '';
        const colorCode = MappedColorCodes[ingeniousColor] || '';
        const colorSurface = powder?.colorSurface?.name ?? '-';

        formattedColors.push({
            key: powder.id + '-' + color?.id,
            id: powder.id,
            color: color?.hex,
            colorName: color?.name,
            powderId: powder?.name,
            powderIdDisplay: stripNaturalWords(powder?.name || ''),
            colorSurface,
            groupName,
            isRoltekColor,
            isStandard,
            ingeniousColor,
            colorObject: color,
            colorCode,
        });
    }

    if (!props.searchQuery) {
        return formattedColors;
    }

    return filterColorsBySeachQuery(formattedColors);
};

const colorsData = computed(() => {
    // If showLatestColors is ON and we have latest colors, use them
    if (props.showLatestColors) {
        return formatLatestColorsData();
    }

    // Otherwise use regular colors
    if (!colors.value.length) {
        return [];
    }

    const formattedColors: ColorTableData[] = [];

    for (let i = 0; i < colors.value.length; i++) {
        const powder = colors.value[i];
        const color = powder?.colors ? powder.colors[0] : null;

        const groupName = powder?.colorCollection?.groupName || '';
        const isRoltekColor = determineIsValidRoltekCollection(powder.colorCollection) && !!color?.ingeniousColor;
        const isStandard = isRoltekColor && groupName === 'STANDARD';
        const ingeniousColor = color?.ingeniousColor || '';
        const colorCode = MappedColorCodes[ingeniousColor] || '';
        const colorSurface = powder?.colorSurface?.name ?? '-';

        if ((props.currentCollection === 'STANDARD' && isStandard) || !isStandard || !props.currentCollection) {
            formattedColors.push({
                key: powder.id + '-' + color?.id,
                id: powder.id,
                color: color?.hex,
                colorName: color?.name,
                powderId: powder?.name,
                powderIdDisplay: stripNaturalWords(powder?.name || ''),
                colorSurface,
                groupName,
                isRoltekColor,
                isStandard,
                ingeniousColor,
                colorObject: color,
                colorCode,
            });
        }
    }

    if (!props.searchQuery) {
        return formattedColors;
    }

    const filteredColors = filterColorsBySeachQuery(formattedColors);
    emit('onQueryChange', filteredColors.length); // Reset pagination when search is applied
    return filteredColors;
});

const selectColor = (selectedColor: ColorTableData) => {
    let selectedPowder;

    if (props.showLatestColors && latestColors.value && latestColors.value.length > 0) {
        // Find the powder from latestColors
        const latestColor = latestColors.value.find((lc) => lc.powder.id === selectedColor.id);
        selectedPowder = latestColor?.powder;
    } else {
        // Find the powder from regular colors
        selectedPowder = colors.value.find((powder) => powder.id === selectedColor.id);
    }

    emit('selectColor', {
        color: selectedColor.colorObject,
        selectedPowder,
        isRoltekColor: selectedColor.isRoltekColor,
        groupName: selectedColor.groupName,
    });
};

const handleResize = () => {
    tableHeight.value = calculateTableHeight();
};

onMounted(() => {
    removePaginationTooltips();
    window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
});

return { __sfc: true,props, emit, tableHeight, customColumns, colors, latestColors, onTableChange, calculateTableHeight, removePaginationTooltips, columns, determineIsValidRoltekCollection, filterColorsBySeachQuery, formatLatestColorsData, colorsData, selectColor, handleResize }
}

})